<script setup lang="ts">
import { computed, onMounted, ref, watchEffect } from "vue"
import { useI18n } from "vue-i18n"
import { useRoute } from "vue-router"

import { AxiosError } from "axios"

import QualityNameInput from "@/components/QualityNameInput.vue"
import Button from "@/components/shared/Button.vue"
import Card from "@/components/shared/Card.vue"
import CardActions from "@/components/shared/CardActions.vue"
import CardContent from "@/components/shared/CardContent.vue"
import CardTitle from "@/components/shared/CardTitle.vue"
import InfoRow from "@/components/shared/InfoRow.vue"
import InfoTable from "@/components/shared/InfoTable.vue"
import InputBox from "@/components/shared/InputBox.vue"
import InputSelect from "@/components/shared/InputSelect.vue"
import LayoutAuthed from "@/components/shared/LayoutAuthed.vue"
import { getLocaleMessages } from "@/locales/index"
import router from "@/plugins/router"
import store from "@/plugins/store"
import qualityService from "@/services/quality"
import type { Option } from "@/types/common.type"
import type { QualityDetail } from "@/types/quality.type"
import { ErrorCode, invalidParameterMessage } from "@/utils/error"
import { normalizeQualityName } from "@/utils/qualityName"
import { reportSentry } from "@/utils/sentry"

const route = useRoute()
const { t } = useI18n()

const item = ref<QualityDetail | null>(null)
const id = computed(() => route.params.id)
const pageTitle = computed(() => {
  return `${t("item.editSpecs")} #${item.value?.name || ""}`
})

const payload = ref({
  name: "",
  hsCode: "",
  countryOfOriginId: 0 as number,
  isPrintOrderable: false,
  minimumWidth: null as number | null,
  fullWidth: null as number | null,
  weight: null as number | null,
  yardWeight: null as number | null,
  categoryId: 0,
  patternId: 0 as number,
  dyeMethodId: 0 as number,
  contentIds: [] as number[],
  contentRatios: [] as number[],
  contents: {} as { id: number; ratio: number }[],
  yarnEffectIds: [] as number[],
  brandYarnIds: [] as number[],
  finishIds: [] as number[],
  appearanceIds: [] as number[],
  performanceIds: [] as number[],
  sustainabilityFactorIds: [] as number[],
})

watchEffect(() => {
  const ids = payload.value.contentIds
  const ratios = payload.value.contentRatios

  payload.value.contents = ids.map((id, idx) => ({
    id: id,
    ratio: ratios[idx] ?? 0, // fallback to 0 if missing
  }))
})

const countryOptions = ref<Option[]>([])
const categoryOptions = ref<Option[]>([])
const patternOptions = ref<Option[]>([])
const dyeMethodOptions = ref<Option[]>([])
const contentOptions = ref<Option[]>([])
const yarnEffectOptions = ref<Option[]>([])
const brandYarnOptions = ref<Option[]>([])
const finishOptions = ref<Option[]>([])
const appearanceOptions = ref<Option[]>([])
const performanceOptions = ref<Option[]>([])
const sustainabilityFactorOptions = ref<Option[]>([])

async function loadCountries() {
  const countries = store.state.countries
  countryOptions.value = countries.map((e) => ({ label: e.name, value: e.id }))
}

async function loadProperties() {
  const data = store.state.properties

  if (data) {
    categoryOptions.value = data.categories.map((e) => ({ label: `${e.rootName} > ${e.name}`, value: e.id }))
    patternOptions.value = data.patterns.map((e) => ({ label: e.name, value: e.id }))
    dyeMethodOptions.value = data.dyeMethods.map((e) => ({ label: e.name, value: e.id }))
    contentOptions.value = data.contents.map((e) => ({ label: `${e.rootName} > ${e.name}`, value: e.id }))
    yarnEffectOptions.value = data.yarnEffects.map((e) => ({ label: e.name, value: e.id }))
    brandYarnOptions.value = data.brandYarns.map((e) => ({ label: e.name, value: e.id }))
    finishOptions.value = data.finishes.map((e) => ({ label: e.name, value: e.id }))
    appearanceOptions.value = data.appearances.map((e) => ({ label: e.name, value: e.id }))
    performanceOptions.value = data.performances.map((e) => ({ label: e.name, value: e.id }))
    sustainabilityFactorOptions.value = data.sustainabilityFactors.map((e) => ({ label: e.name, value: e.id }))
  }
}

async function loadItem() {
  item.value = await qualityService.get(Number(id.value))

  payload.value.name = item.value.name
  payload.value.hsCode = item.value.hsCode
  payload.value.countryOfOriginId = item.value.countryOfOrigin?.id || 0
  payload.value.isPrintOrderable = item.value.isPrintOrderable
  payload.value.minimumWidth = item.value.minimumWidth
  payload.value.fullWidth = item.value.fullWidth
  payload.value.weight = item.value.weight
  payload.value.yardWeight = item.value.yardWeight
  payload.value.categoryId = item.value.category?.id
  payload.value.patternId = item.value.patterns[0]?.id || 0 // TODO pattern should be singular
  payload.value.dyeMethodId = item.value.dyeMethod?.id || 0
  payload.value.contentIds = item.value.contents.map((e) => e.id)
  payload.value.contentRatios = item.value.contents.map((e) => e.ratio)
  payload.value.yarnEffectIds = item.value.yarnEffects.map((e) => e.id)
  payload.value.brandYarnIds = item.value.brandYarns.map((e) => e.id)
  payload.value.finishIds = item.value.finishes.map((e) => e.id)
  payload.value.appearanceIds = item.value.appearances.map((e) => e.id)
  payload.value.performanceIds = item.value.performances.map((e) => e.id)
  payload.value.sustainabilityFactorIds = item.value.sustainabilityFactors.map((e) => e.id)
}

onMounted(async () => {
  try {
    await loadCountries()
    await loadProperties()
    await loadItem()
  } catch (error) {
    console.error(error)
    reportSentry(error)
    router.replace("/items")
  }
})

const isSubmitting = ref(false)
const errorMessage = ref("")

const calculateWeight = computed(() => {
  if (payload.value.fullWidth && payload.value.yardWeight) {
    return Math.floor(((payload.value.yardWeight / payload.value.fullWidth / 2.54) * 100) / 0.9144)
  } else {
    return "-"
  }
})

async function saveItem() {
  if (isSubmitting.value) return
  errorMessage.value = ""
  payload.value.name = normalizeQualityName(payload.value.name)
  if (!payload.value.name) {
    errorMessage.value = t("item.nameRequired")
    return
  }
  isSubmitting.value = true

  try {
    await qualityService.update(Number(id.value), payload.value)

    router.push(`/items/${id.value}`)
  } catch (err) {
    console.log(err)
    reportSentry(err)
    errorMessage.value = err instanceof AxiosError && err.response?.data?.code === ErrorCode.INVALID_PARAMETER
      ? invalidParameterMessage(err.response.data, {
          duplicateName: t("item.errors.duplicateName"),
          unknown: t("item.errors.invalidParameter"),
        })
      : t("shared.errors.unknown")
    isSubmitting.value = false
  }
}
</script>

<template>
  <LayoutAuthed :title="pageTitle">
    <div class="row">
      <div class="col-12 col-lg-9 col-xxl-6">
        <form @submit.prevent="saveItem()">
          <Card>
            <CardTitle>{{ $t("item.basic") }}</CardTitle>
            <CardContent>
              <QualityNameInput v-model="payload.name" />
              <InputBox v-model="payload.hsCode" type="text" field="hsCode" :label="$t('item.hsCode')" />
              <InputSelect
                v-model="payload.countryOfOriginId"
                field="countryOfOriginId"
                :label="$t('item.countryOfOrigin')"
                :options="countryOptions"
                searchable
                required
              />
              <InputSelect
                v-model="payload.isPrintOrderable"
                field="isPrintOrderable"
                :label="$t('item.isPrintOrderable')"
                :options="Object.entries(getLocaleMessages().shared.boolean).map(([v, k]) => ({ label: k, value: v }))"
                required
              />
            </CardContent>
            <CardTitle>{{ $t("item.dimensions") }}</CardTitle>
            <CardContent>
              <InputBox
                v-model.number="payload.minimumWidth"
                type="number"
                field="minimumWidth"
                :label="`${$t('item.minimumWidth')} (${$t('item.units.minimumWidth')})`"
                :min="30"
                :max="70"
                required
              />
              <InputBox
                v-model.number="payload.fullWidth"
                type="number"
                field="fullWidth"
                :label="`${$t('item.fullWidth')} (${$t('item.units.fullWidth')})`"
                :min="30"
                :max="70"
              />
              <InputBox
                v-model.number="payload.weight"
                type="number"
                field="weight"
                :label="`${$t('item.weight')} (${$t('item.units.weight')})`"
                :min="30"
                :max="1000"
                required
              />
              <div class="mt-1 mb-3 text-danger">
                {{
                  `${$t("item.weightCalculator")} (${$t("item.fullWidth")}, ${$t("item.yardWeight")}) : ${calculateWeight} ${$t("item.units.weight")}`
                }}
              </div>
              <InputBox
                v-model.number="payload.yardWeight"
                type="number"
                field="yardWeight"
                :label="`${$t('item.yardWeight')} (${$t('item.units.yardWeight')})`"
                :min="30"
                :max="1000"
              />
            </CardContent>
            <CardTitle>{{ $t("item.tags") }}</CardTitle>
            <CardContent>
              <InputSelect
                v-model="payload.categoryId"
                field="categoryId"
                :label="$t('item.category')"
                :options="categoryOptions"
                searchable
                required
              />
              <InputSelect
                v-model="payload.patternId"
                field="patternId"
                :label="$t('item.pattern')"
                :options="patternOptions"
                searchable
                required
              />
              <InputSelect
                v-model="payload.dyeMethodId"
                field="dyeMethodId"
                :label="$t('item.dyeMethod')"
                :options="dyeMethodOptions"
                searchable
                required
              />
              <InputSelect
                v-model="payload.contentIds"
                field="contentIds[]"
                :label="$t('item.contents')"
                :options="contentOptions"
                searchable
                multiple
                required
              />
              <InfoTable class="my-2 ms-3">
                <InfoRow
                  v-for="(contentId, idx) in payload.contentIds"
                  :name="contentOptions.find((e) => e.value === contentId)!.label"
                >
                  <InputBox v-model="payload.contentRatios[idx]!" type="number" field="contentRatios[]" prefix="%" />
                </InfoRow>
              </InfoTable>
              <InputSelect
                v-model="payload.yarnEffectIds"
                field="yarnEffectIds[]"
                :label="$t('item.yarnEffects')"
                :options="yarnEffectOptions"
                searchable
                multiple
              />
              <InputSelect
                v-model="payload.brandYarnIds"
                field="brandYarnIds[]"
                :label="$t('item.brandYarns')"
                :options="brandYarnOptions"
                searchable
                multiple
              />
              <InputSelect
                v-model="payload.finishIds"
                field="finishIds[]"
                :label="$t('item.finishes')"
                :options="finishOptions"
                searchable
                multiple
              />
              <InputSelect
                v-model="payload.appearanceIds"
                field="appearanceIds[]"
                :label="$t('item.appearances')"
                :options="appearanceOptions"
                searchable
                multiple
              />
              <InputSelect
                v-model="payload.performanceIds"
                field="performanceIds[]"
                :label="$t('item.performances')"
                :options="performanceOptions"
                searchable
                multiple
              />
              <InputSelect
                v-model="payload.sustainabilityFactorIds"
                field="sustainabilityFactorIds[]"
                :label="$t('item.sustainabilityFactors')"
                :options="sustainabilityFactorOptions"
                searchable
                multiple
              />
            </CardContent>
            <CardActions displayBlock>
              <Button type="submit" color="dark" :text="$t('shared.save')" :isSubmitting="isSubmitting" />
              <span class="text-center text-danger error-message">{{ errorMessage }}</span>
            </CardActions>
          </Card>
        </form>
      </div>
    </div>
  </LayoutAuthed>
</template>

<style scoped>
.error-message {
  font-size: 16px;
  font-weight: bold;
}
</style>
